import React, { ChangeEvent, ReactElement, useEffect, useState } from "react";
import Text from "@components/shared/text";
import Button from "@components/shared/form/button";
import styles from "./style.module.scss";
import { useForm } from "react-hook-form";
import { submitContactUsForm } from "src/api/contactUs";
import ReCAPTCHA from "react-google-recaptcha";
import useWindowSize from "@hooks/useWindowSize";
import ConfirmationCheck from "@components/shared/confirmationCheck";
import { translate } from "@helpers/translationObjectFormatter";
import { TranslationsObject } from "@genericTypes/sharedTypes";
import { useQuery } from "@tanstack/react-query";
import { handleUsPhoneNumberMasking } from "@lib/sharedUtils";

type Inputs = {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;

    subject: string;
    description: string;
};

export default function Form(): ReactElement {
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);

    const window = useWindowSize();

    const [recaptcha, setRecaptcha] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const {
        register,
        handleSubmit,

        formState: { errors },
    } = useForm<Inputs>();

    const submitHandler = async (info: Inputs) => {
        setLoading(true);

        const { data } = await submitContactUsForm({
            fields: {
                email: info.email,
                firstName: info.firstName,
                lastName: info.lastName,
                phone: info.phone,

                subject: info.subject,
                body: info.description,
                googleReCaptcha: recaptcha ?? "",
            },
        });
        setLoading(false);

        if (data?.success) {
            setIsSubmitted(true);
        } else {
            setError(data?.code as string);
        }
    };
    useEffect(() => {
        if (window.width < 640) {
            setIsMobile(true);
        } else {
            setIsMobile(false);
        }
    }, [window.width]);

    return (
        <>
            {!isSubmitted ? (
                <div className=" lg:w-1/2 w-full px-5 sm:w-fit">
                    <Text
                        type="title"
                        className="lg:text-4xl  lg:leading-[63px] font-bold  mt-7 text-center sm:text-start text-xl"
                    >
                        {translate(
                            translations,
                            "several_partnerWithUsPage_letsTalk",
                        )}
                    </Text>
                    <Text className="lg:text-lg lg:leading-7  mt-4 max-w-[520px]  text-center sm:text-start">
                        {translate(
                            translations,
                            "several_contactUS_mainFormDescription",
                        )}
                    </Text>
                    <form
                        onSubmit={handleSubmit(submitHandler)}
                        className="bg-white px-9 max-w-[520px] pt-14 pb-8 mt-5 mb-11 rounded-lg "
                    >
                        <div className={` ${styles["innerInput"]}`}>
                            <div>
                                <input
                                    type="text"
                                    placeholder={translate(
                                        translations,
                                        "several_contactUS_formPlaceHolderFirstName",
                                    )}
                                    {...register("firstName", {
                                        required: true,
                                    })}
                                />
                                {errors.firstName &&
                                    errors.firstName.type === "required" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formError",
                                            )}
                                        </span>
                                    )}
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder={translate(
                                        translations,
                                        "several_contactUS_formPlaceHolderLastName",
                                    )}
                                    {...register("lastName", {
                                        required: true,
                                    })}
                                />
                                {errors.lastName &&
                                    errors.lastName.type === "required" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formError",
                                            )}
                                        </span>
                                    )}
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder={translate(
                                        translations,
                                        "several_contactUS_formPlaceHolderEmail",
                                    )}
                                    {...register("email", {
                                        required: true,
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: "invalid email address",
                                        },
                                    })}
                                />
                                {errors.email &&
                                    errors.email.type === "required" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formError",
                                            )}
                                        </span>
                                    )}
                                {errors.email &&
                                    errors.email.type === "pattern" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorInvalidEmailAddress",
                                            )}
                                        </span>
                                    )}
                            </div>
                            <div>
                                <input
                                    placeholder={translate(
                                        translations,
                                        "several_contactUS_formPlaceHolderPhone",
                                    )}
                                    {...register("phone", {
                                        required: true,
                                        onChange: (
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) => {
                                            const { value } = e.target;
                                            e.target.value =
                                                handleUsPhoneNumberMasking(
                                                    value,
                                                );
                                        },
                                        minLength: 10,
                                    })}
                                    type="tel"
                                />
                                {errors.phone &&
                                    errors.phone.type === "required" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formError",
                                            )}
                                        </span>
                                    )}
                                {errors.phone &&
                                    errors.phone.type === "minLength" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorMinimumDigits",
                                            )}
                                        </span>
                                    )}
                                {errors.phone &&
                                    errors.phone.type === "maxLength" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorPhoneNumberMaximumDigits",
                                            )}
                                        </span>
                                    )}
                                {errors.phone &&
                                    errors.phone.type === "pattern" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorInvalidPhoneNumber",
                                            )}
                                        </span>
                                    )}
                            </div>
                        </div>
                        <span className={`${styles["subjectInput"]}`}>
                            <input
                                type="text"
                                placeholder={translate(
                                    translations,
                                    "several_contactUS_formPlaceHolderSubject",
                                )}
                                {...register("subject", { required: true })}
                            />
                        </span>
                        {errors.subject &&
                            errors.subject.type === "required" && (
                                <span className={styles[`errorColor`]}>
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formError",
                                    )}
                                </span>
                            )}
                        <label className="block mt-7" htmlFor="description">
                            {translate(
                                translations,
                                "several_category_sideWidget_description",
                            )}
                        </label>
                        <span
                            className={`${styles["descriptionInput"]} mb-3 block`}
                        >
                            <input
                                id="description"
                                type="text"
                                {...register("description", { required: true })}
                                placeholder={translate(
                                    translations,
                                    "several_partnerWithUsPage_formPlaceholderWouldLikeUsKnow",
                                )}
                            />
                        </span>
                        {errors.description &&
                            errors.description.type === "required" && (
                                <span
                                    className={`${
                                        styles[`errorColor`]
                                    } -mt-3 block mb-3`}
                                >
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formError",
                                    )}
                                </span>
                            )}
                        <div className="w-fit mx-auto sm:w-full">
                            {isMobile ? (
                                <ReCAPTCHA
                                    sitekey={`${
                                        process.env
                                            .NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY as string
                                    }`}
                                    onChange={(value) => {
                                        setRecaptcha(value as string);
                                    }}
                                    size="compact"
                                />
                            ) : (
                                <ReCAPTCHA
                                    sitekey={`${
                                        process.env
                                            .NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY as string
                                    }`}
                                    onChange={(value) => {
                                        setRecaptcha(value as string);
                                    }}
                                    size="normal"
                                />
                            )}
                        </div>
                        {error ? (
                            error === "INVALID_PHONE_NUMBER_FORMAT" ? (
                                <span
                                    className={`${
                                        styles[`errorColor`]
                                    } mt-3  block -mb-3`}
                                >
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formErrorInvalidPhoneNumberFormat",
                                    )}
                                </span>
                            ) : (
                                <span
                                    className={`${
                                        styles[`errorColor`]
                                    } mt-3  block -mb-3 `}
                                >
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formErrorPublic",
                                    )}
                                </span>
                            )
                        ) : (
                            <></>
                        )}

                        <span className="block lg:w-[40%] mx-auto mt-10">
                            <Button
                                type="submit"
                                onClick={() => {}}
                                className="py-4"
                                loading={loading}
                            >
                                {translate(
                                    translations,
                                    "several_contactUS_mainTitle",
                                )}
                            </Button>
                        </span>
                    </form>
                </div>
            ) : (
                <div className=" lg:w-1/2 w-full px-5   sm:w-fit mt-10 lg:mt-0">
                    <div className=" w-fit mx-auto">
                        <ConfirmationCheck color="black" />
                    </div>

                    <div className="flex-col justify-center items-center text-center flex">
                        <h2
                            style={{ color: "black" }}
                            className="text-3xl font-bold"
                        >
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formSubmitThankYou",
                            )}
                        </h2>
                        <p
                            className="font-semibold mt-4"
                            style={{ color: "black" }}
                        >
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formSubmitYourMessageSent",
                            )}
                        </p>
                        <p className="text-sm mt-1" style={{ color: "black" }}>
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formSubmitOurSupportStaff",
                            )}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}
